import { InjectionToken, Type } from '@angular/core';
import { JobDashboardRange } from '../pages/dashboard/widgets/job-dashboard/job-dashboard-configuration';

export enum DashboardComponentSize {
  THIRD = 'col-lg-4',
  HALF = 'col-lg-6',
  TWO_THIRDS = 'col-lg-8',
  FULL = 'col-lg-12',
}

export interface DashboardComponentConfiguration {
  component: string;
  size: DashboardComponentSize;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  configuration: any;
}

export interface Dashboard {
  id?: string;
  configuration: { items: DashboardComponentConfiguration[] };
}

export type DashboardEditorTarget =
  { type: 'organization' } | { type: 'workflowTemplate'; templateId: string };

export interface WorkflowResultFieldDefinition {
  key: string;
  type: 'STRING' | 'NUMBER' | 'BOOLEAN' | 'TIMESTAMP';
}

export interface DashboardFieldInfo {
  key: string;
  inferredType: string;
  occurrenceCount: number;
  sampleValues: string[];
}

export interface DashboardEditorContext {
  target: DashboardEditorTarget;
  resultFields: WorkflowResultFieldDefinition[];
}

export const DASHBOARD_EDITOR_CONTEXT =
  new InjectionToken<DashboardEditorContext>('DASHBOARD_EDITOR_CONTEXT');

export function isWorkflowTemplateDashboard(
  context: DashboardEditorContext | null,
): boolean {
  return context?.target.type === 'workflowTemplate';
}

export function getDeclaredResultFields(
  context: DashboardEditorContext | null,
): DashboardFieldInfo[] {
  return (context?.resultFields ?? []).map((field) => ({
    key: field.key,
    inferredType: field.type.toLowerCase(),
    occurrenceCount: 0,
    sampleValues: [],
  }));
}

export interface DashboardComponentInterface {
  setConfiguration(configuration: unknown): void;
  loadData(): void;
}

export interface DashboardComponentPair {
  view: Type<DashboardComponentInterface>;
  form: Type<unknown>;
}

export function filterAvailableDataConnectionIds(
  dataConnectionIds: unknown,
  allowedDataConnectionIds: string[],
): string[] {
  if (!Array.isArray(dataConnectionIds)) {
    return [];
  }

  const allowedIds = new Set(allowedDataConnectionIds);
  return dataConnectionIds.filter(
    (id): id is string => typeof id === 'string' && allowedIds.has(id),
  );
}

const DEFAULT_DASHBOARD_CONFIG: Dashboard['configuration'] = {
  items: [
    {
      component: 'SmartwatchDashboardComponent',
      size: DashboardComponentSize.THIRD,
      configuration: {
        title: 'Smartwatches',
        description: 'Registrierte Smartwatches',
      },
    },
    {
      component: 'WorkerDashboardComponent',
      size: DashboardComponentSize.THIRD,
      configuration: {
        title: 'Benutzer',
        description: 'Registrierte Benutzer',
      },
    },
    {
      component: 'JobDashboardComponent',
      size: DashboardComponentSize.THIRD,
      configuration: {
        dataConnectionIds: [],
        title: 'Aufträge',
        description: 'Übersicht über alle Aufträge',
        range: JobDashboardRange.ALL_TIME,
      },
    },
    {
      component: 'WorkflowDashboardComponent',
      size: DashboardComponentSize.FULL,
      configuration: {
        title: 'Workflows',
      },
    },
  ],
};

export function createDefaultDashboardConfig(): Dashboard['configuration'] {
  return JSON.parse(
    JSON.stringify(DEFAULT_DASHBOARD_CONFIG),
  ) as Dashboard['configuration'];
}

export function normalizeDashboard(
  dashboard:
    | { id?: string; configuration?: Partial<Dashboard['configuration']> }
    | null
    | undefined,
  fallbackConfiguration: Dashboard['configuration'] = { items: [] },
): Dashboard {
  return {
    ...dashboard,
    configuration: {
      items: Array.isArray(dashboard?.configuration?.items)
        ? dashboard.configuration.items.map((item) =>
            item.configuration?.range === 'last_24_hours'
              ? {
                  ...item,
                  configuration: {
                    ...item.configuration,
                    range: JobDashboardRange.LAST_DAY,
                  },
                }
              : item,
          )
        : fallbackConfiguration.items,
    },
  };
}
